import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { formatToCNPJ } from 'brazilian-values';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { AccountInfoLabel, BoxFlex, ErrorIcon } from '@woovi/ui';
import { obscureCNPJ } from '@woovi/utils';

import type { PaymentDeleted_charge$key } from './__generated__/PaymentDeleted_charge.graphql.ts';
import type { PaymentDeleted_pixQrCode$key } from './__generated__/PaymentDeleted_pixQrCode.graphql.ts';
import PaymentLinkDeletedValue from './paymentLinkDeleted/PaymentLinkDeletedValue.tsx';
import PaymentLinkAdditionalInfo from '../paymentLink/PaymentLinkAdditionalInfo.tsx';

import type { ReactNode } from 'react';

type PaymentDeletedProps = {
  charge: PaymentDeleted_charge$key;
  pixQrCode: PaymentDeleted_pixQrCode$key;
};

const PaymentDeleted = (props: PaymentDeletedProps): ReactNode => {
  const theme = useTheme();
  const { t } = useTranslation();

  const charge = useFragment<PaymentDeleted_charge$key>(
    graphql`
      fragment PaymentDeleted_charge on Charge {
        identifier
        company @required(action: LOG) {
          nameFriendly
          taxID {
            taxID
          }
        }
        ...PaymentLinkDeletedValue_charge
        ...PaymentLinkAdditionalInfo_charge
      }
    `,
    props.charge,
  );

  const pixQrCode = useFragment<PaymentDeleted_pixQrCode$key>(
    graphql`
      fragment PaymentDeleted_pixQrCode on PixQrCode {
        identifier
        company @required(action: LOG) {
          nameFriendly
          taxID {
            taxID
          }
        }
        ...PaymentLinkDeletedValue_pixQrCode
      }
    `,
    props.pixQrCode,
  );

  const getIdentifier = () => {
    if (pixQrCode?.identifier) {
      return (
        <AccountInfoLabel label={t('Identifier')} text={pixQrCode.identifier} />
      );
    }

    if (charge?.identifier) {
      return (
        <AccountInfoLabel label={t('Identifier')} text={charge.identifier} />
      );
    }

    return null;
  };

  const getTitle = () => {
    if (pixQrCode) {
      return t('QR Code deleted');
    }

    return t('Charge deleted');
  };

  const getDescription = () => {
    if (pixQrCode) {
      return t('This QR Code was deleted');
    }

    return t('This charge was deleted');
  };

  const getCnpj = () => {
    if (pixQrCode) {
      return formatToCNPJ(pixQrCode?.company.taxID.taxID);
    }

    return formatToCNPJ(charge?.company.taxID.taxID);
  };

  const getCompanyFriendlyName = () => {
    if (pixQrCode) {
      return pixQrCode?.company.nameFriendly;
    }

    return charge?.company.nameFriendly;
  };

  return (
    <Stack spacing={2}>
      <BoxFlex sx={{ flexDirection: 'column', alignItems: 'center' }}>
        <ErrorIcon />
      </BoxFlex>
      <Typography
        variant={'h2'}
        sx={{
          fontSize: '22px',
          fontWeight: '800',
          textAlign: 'center',
          color: theme.palette.secondary.main,
        }}
      >
        {getTitle()}
      </Typography>
      <Typography
        variant={'h5'}
        sx={{
          fontSize: '16px',
          textAlign: 'center',
        }}
      >
        {getDescription()}
      </Typography>
      <PaymentLinkDeletedValue charge={charge} pixQrCode={pixQrCode} />
      <AccountInfoLabel
        label={t('Addressee')}
        text={getCompanyFriendlyName()}
      />
      <AccountInfoLabel text={obscureCNPJ(formatToCNPJ(getCnpj()))} />
      {getIdentifier()}
      <PaymentLinkAdditionalInfo charge={charge} />
    </Stack>
  );
};

export default PaymentDeleted;
